import { GEOMETRY_TOLERANCES } from "../../config/tolerances";
import {
  dotProduct,
  subtractVectors,
  normalizeVector,
  perpendicularVector,
  addVectors,
  scaleVector,
  distance
} from "./vectorMath";

const COLLISION_EPSILON = 1e-1;

/**
 * 2D Polygon & Line Segment Intersection Utilities (SAT & Ray-casting)
 */

export function projectPolygonOntoAxis(axis, vertices) {
  let min = dotProduct(axis, vertices[0]);
  let max = min;
  for (let i = 1; i < vertices.length; i++) {
    const p = dotProduct(axis, vertices[i]);
    if (p < min) min = p;
    else if (p > max) max = p;
  }
  return { min, max };
}

export function checkPolygonsIntersectionSAT(poly1Vertices, poly2Vertices) {
  if (
    !poly1Vertices ||
    !poly2Vertices ||
    poly1Vertices.length < 3 ||
    poly2Vertices.length < 3
  ) {
    return false;
  }

  const getAxes = (vertices) => {
    const axes = [];
    for (let i = 0; i < vertices.length; i++) {
      const p1 = vertices[i];
      const p2 = vertices[(i + 1) % vertices.length];
      const edge = subtractVectors(p2, p1);
      const normal = normalizeVector(perpendicularVector(edge));
      if (normal.x !== 0 || normal.y !== 0) {
        axes.push(normal);
      }
    }
    return axes;
  };

  const axes1 = getAxes(poly1Vertices);
  const axes2 = getAxes(poly2Vertices);

  for (const axis of [...axes1, ...axes2]) {
    const proj1 = projectPolygonOntoAxis(axis, poly1Vertices);
    const proj2 = projectPolygonOntoAxis(axis, poly2Vertices);

    if (
      proj1.max <= proj2.min + COLLISION_EPSILON ||
      proj2.max <= proj1.min + COLLISION_EPSILON ||
      proj1.max <= proj2.min - COLLISION_EPSILON ||
      proj2.max <= proj1.min - COLLISION_EPSILON
    ) {
      return false;
    }
  }

  return true;
}

export function findNearestPointOnLineSegment(point, start, end) {
  const lineVec = subtractVectors(end, start);
  const pointVec = subtractVectors(point, start);
  const lineLenSq = dotProduct(lineVec, lineVec);

  if (lineLenSq < GEOMETRY_TOLERANCES.CALCULATION_EPSILON) {
    return { point: start, distance: distance(point, start), t: 0 };
  }

  let t = dotProduct(pointVec, lineVec) / lineLenSq;
  t = Math.max(0, Math.min(1, t));

  const nearestPoint = addVectors(start, scaleVector(lineVec, t));
  const dist = distance(point, nearestPoint);

  return { point: nearestPoint, distance: dist, t };
}

function onSegment(p, q, r) {
  return (
    q.x <= Math.max(p.x, r.x) &&
    q.x >= Math.min(p.x, r.x) &&
    q.y <= Math.max(p.y, r.y) &&
    q.y >= Math.min(p.y, r.y)
  );
}

function orientation(p, q, r) {
  const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
  if (Math.abs(val) < GEOMETRY_TOLERANCES.CALCULATION_EPSILON) return 0;
  return val > 0 ? 1 : 2;
}

export function lineSegmentsIntersect(p1, q1, p2, q2) {
  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);

  if (o1 !== o2 && o3 !== o4) {
    const eps = GEOMETRY_TOLERANCES.CALCULATION_EPSILON;
    const atP1 = Math.abs(p1.x - p2.x) < eps && Math.abs(p1.y - p2.y) < eps;
    const atQ1 = Math.abs(q1.x - q2.x) < eps && Math.abs(q1.y - q2.y) < eps;
    const atP1_Q2 = Math.abs(p1.x - q2.x) < eps && Math.abs(p1.y - q2.y) < eps;
    const atQ1_P2 = Math.abs(q1.x - p2.x) < eps && Math.abs(q1.y - p2.y) < eps;
    if (atP1 || atQ1 || atP1_Q2 || atQ1_P2) return false;

    return true;
  }

  return false;
}

export function getLineIntersectionPoint(p1, q1, p2, q2) {
  const x1 = p1.x, y1 = p1.y;
  const x2 = q1.x, y2 = q1.y;
  const x3 = p2.x, y3 = p2.y;
  const x4 = q2.x, y4 = q2.y;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  
  if (Math.abs(denom) < 1e-10) {
    return null;
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    const eps = GEOMETRY_TOLERANCES.CALCULATION_EPSILON;
    const isAtP1 = Math.abs(t) < eps;
    const isAtQ1 = Math.abs(t - 1) < eps;
    const isAtP2 = Math.abs(u) < eps;
    const isAtQ2 = Math.abs(u - 1) < eps;

    if (isAtP1 || isAtQ1 || isAtP2 || isAtQ2) {
      return null;
    }

    return {
      x: x1 + t * (x2 - x1),
      y: y1 + t * (y2 - y1)
    };
  }

  return null;
}
